// Helpers de cartas — comparacao, manilha, cores. Funcoes puras
// usadas pelos componentes pra renderizar.

import type { Card } from '../stores/match';

export type Suit = '♣' | '♥' | '♠' | '♦';
export type Value = '4' | '5' | '6' | '7' | 'Q' | 'J' | 'K' | 'A' | '2' | '3';

const SUIT_RED: ReadonlySet<string> = new Set(['♥', '♦']);
export function isRedSuit(suit: string | undefined): boolean {
  return !!suit && SUIT_RED.has(suit);
}

// Cycle de valores no truco — usado pra calcular manilha (valor +1).
const VALUE_CYCLE = ['4', '5', '6', '7', 'Q', 'J', 'K', 'A', '2', '3'] as const;

// nextValue retorna o proximo valor no cycle do truco. Vira A4 -> manilha 5.
export function nextValue(v: string | undefined): string | null {
  if (!v) return null;
  const idx = VALUE_CYCLE.indexOf(v as Value);
  if (idx < 0) return null;
  return VALUE_CYCLE[(idx + 1) % VALUE_CYCLE.length];
}

// Forca natural (sem considerar manilha): 4 = 1, 5 = 2, ..., 3 = 10
const NATURAL_RANK: Record<string, number> = {
  '4': 1, '5': 2, '6': 3, '7': 4, 'Q': 5,
  'J': 6, 'K': 7, 'A': 8, '2': 9, '3': 10,
};

// Manilha rank por naipe (paus > copas > espadas > ouros).
const MANILHA_RANK: Record<string, number> = {
  '♣': 4, '♥': 3, '♠': 2, '♦': 1,
};

// cardStrength retorna um numero 0-100+ pra comparar duas cartas dado
// o vira (que determina a manilha). Cards manilha pulam pra 90+ —
// sempre vencem nao-manilhas.
export function cardStrength(card: Card | undefined, vira: Card | undefined | null): number {
  if (!card || !card.value || !card.suit) return -1;
  if (card.flipped) return -1; // virada de baixo sempre perde
  const manilhaVal = nextValue(vira?.value);
  if (manilhaVal && card.value === manilhaVal) {
    // manilha — rank base 90 + suit rank
    return 90 + (MANILHA_RANK[card.suit] ?? 0);
  }
  return NATURAL_RANK[card.value] ?? 0;
}

// countPlays conta o total de cartas jogadas no snap atual (1v1 e 2v2).
// Usado pra detectar "primeiro trick" — momento em que a regra do truco
// PROIBE jogar carta encoberta (face-down).
import type { MatchSnapshot } from '../stores/match';

export function countPlays(snap: MatchSnapshot | null | undefined): number {
  if (!snap) return 0;
  const players = snap.format === '2v2'
    ? [...(snap.team_a || []), ...(snap.team_b || [])]
    : [snap.p1, snap.p2].filter(Boolean);
  let n = 0;
  for (const p of players) {
    if (!p) continue;
    if (p.first_play && p.first_play.value) n++;
    if (p.second_play && p.second_play.value) n++;
    if (p.third_play && p.third_play.value) n++;
  }
  return n;
}

// isFirstTrick: true quando estamos dentro do 1o trick da mão atual.
// Regra canonica do truco: carta encoberta SO a partir da 2a vaza.
//
// Prefere o campo tricks_closed (backend desde 2026-05-18) que sempre
// reporta exatamente quantas vazas fecharam — independente do snapshot
// delta. Fallback: contar plays manualmente (servia ate B1 sprint;
// agora retorna sempre <=4 em 2v2 porque snapshot delta so popula a
// vaza atual).
export function isFirstTrick(snap: MatchSnapshot | null | undefined): boolean {
  if (!snap) return true;
  if (typeof snap.tricks_closed === 'number') {
    return snap.tricks_closed === 0;
  }
  // Fallback compat com backend antigo: conta plays
  const threshold = snap.format === '2v2' ? 4 : 2;
  return countPlays(snap) < threshold;
}

// nextEscalationWord pega o valor ATUAL da mao (1/3/6/9/12) e devolve
// o nome do PROXIMO nivel pra renderizar no botao/modal de truco.
export function nextEscalationWord(currentValue: number): string {
  switch (currentValue) {
    case 1: return 'TRUCO';
    case 3: return 'SEIS';
    case 6: return 'NOVE';
    case 9: return 'DOZE';
    default: return `${currentValue}`;
  }
}

// pendingValueAfterAccept: se voce aceita o truco pendente, a mao
// passa a valer X. Backend usa esse mesmo step ladder.
export function pendingValueAfterAccept(current: number): number {
  switch (current) {
    case 1: return 3;
    case 3: return 6;
    case 6: return 9;
    case 9: return 12;
    default: return current;
  }
}

// nextEscalationValue: valor a que a mao subiria se o responder
// raise em vez de aceitar.
export function nextEscalationValue(current: number): number {
  switch (current) {
    case 1: return 6;
    case 3: return 6;
    case 6: return 9;
    case 9: return 12;
    default: return current;
  }
}

// escalationName: nome humano do truco no valor dado. Felipe/Leonardo
// 2026-05-20: "inves de +6, aparecer so SEIS".
export function escalationName(value: number): string {
  switch (value) {
    case 3: return 'TRUCO';
    case 6: return 'SEIS';
    case 9: return 'NOVE';
    case 12: return 'DOZE';
    default: return String(value);
  }
}
